<template>
  <el-config-provider :locale="elementLocale">
    <div class="app-container">
      <auth-monitor />
      <network-status />
      <router-view v-slot="{ Component }">
        <component :is="Component" @vue:error="handleComponentError" />
      </router-view>
    </div>
  </el-config-provider>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import en from 'element-plus/es/locale/lang/en'
import NetworkStatus from '@/components/common/NetworkStatus.vue';
import AuthMonitor from '@/components/common/AuthMonitor.vue';
import { useUserStore } from '@/stores/user'
import { getCurrentUser } from '@/api/users'
import { useAuthStore } from '@/stores/auth'
import { useMembershipStore } from '@/stores/membership'
const userStore = useUserStore()
const route = useRoute()
const authStore = useAuthStore()
const membershipStore = useMembershipStore()
const { locale } = useI18n()
const elementLocale = computed(() => locale.value === 'zh-CN' ? zhCn : en)

watch(
  locale,
  (lang) => {
    document.documentElement.lang = lang || 'zh-CN'
  },
  { immediate: true }
)

// 处理组件错误
const handleComponentError = (err, instance, info) => {
  console.error('组件错误捕获:', err)
  console.error('错误信息:', info)
  console.error('组件实例:', instance)
  
  // 防止错误冒泡导致应用崩溃
  return false
}

const updateUserInfo = async () => {
  // 如果是无需认证的页面或用户未认证，不调用接口
  const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/activate']
  if (publicPaths.includes(route.path) || !authStore.isAuthenticated) {
    console.log('跳过用户信息更新，当前路径:', route.path, '认证状态:', authStore.isAuthenticated)
    return
  }
  
  // 防止重复请求：检查是否正在请求中
  if (window._isUpdatingUserInfo) {
    console.log('用户信息正在更新中，跳过重复请求')
    return
  }
  
  // 防止重复请求：检查最近是否已更新（30秒内）
  const lastUpdateTime = window._lastUserInfoUpdateTime || 0
  const now = Date.now()
  if (now - lastUpdateTime < 30000) {
    console.log('用户信息刚刚更新过，跳过重复请求')
    return
  }
  
  try {
    window._isUpdatingUserInfo = true
    const response = await getCurrentUser()
    const { current_tenant, ...restUserData } = response
    
    // 重组用户信息，将 current_tenant_info 改为 tenant
    const userInfo = {
      ...restUserData,
      tenant: current_tenant
    }

    localStorage.setItem('user', JSON.stringify(userInfo))
    localStorage.setItem('user_info', JSON.stringify(userInfo))
    localStorage.setItem('current_tenant', JSON.stringify(current_tenant))
    localStorage.setItem('current_tenant_id', String(restUserData.current_tenant_id))

    userStore.setUserInfo(userInfo)
    window._lastUserInfoUpdateTime = Date.now()
  } catch (error) {
    console.error('更新用户信息失败:', error)
  } finally {
    window._isUpdatingUserInfo = false
  }
}

// 更新会员信息
const updateMembershipInfo = async () => {
  // 公共页面检查 - 只排除登录、注册等真正的公共页面，不排除首页
  const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/activate']
  
  console.log('🔍 [App] 检查会员信息更新条件:', {
    currentPath: route.path,
    isPublicPath: publicPaths.includes(route.path),
    isAuthenticated: authStore.isAuthenticated
  })
  
  if (publicPaths.includes(route.path) || !authStore.isAuthenticated) {
    console.log('🔒 [App] 跳过会员信息更新，当前路径:', route.path, '认证状态:', authStore.isAuthenticated)
    return
  }
  
  try {
    console.log('🔄 [App] 开始更新会员信息...')
    
    // 每次都获取最新数据
    await membershipStore.fetchMembership()
    
    console.log('✅ [App] 会员信息已更新:', {
      tierCode: membershipStore.tierCode,
      tierName: membershipStore.tierName,
      canDownloadPdf: membershipStore.canDownloadPdf,
      canDownloadWordMd: membershipStore.canDownloadWordMd
    })
  } catch (error) {
    console.error('❌ [App] 更新会员信息失败:', error)
    // 失败不阻塞应用，会使用localStorage中的缓存数据
  }
}

onMounted(async () => {
  // 只在已认证时才尝试更新用户信息和会员信息
  if (authStore.isAuthenticated) {
    await Promise.all([
      updateUserInfo(),
      updateMembershipInfo()
    ])
  }
  console.log('应用已加载');
});
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  font-family: 'Microsoft YaHei', 'PingFang SC', 'Noto Sans SC', Arial, sans-serif;
}

#app {
  height: 100%;
  scrollbar-color: #dcdfe6 transparent;
}

.app-container {
  display: flex;
  flex-direction: column;
}

/* 全局样式：控制日期时间不换行 */
.el-descriptions__cell .el-descriptions__label,
.el-descriptions__cell .el-descriptions__content,
.datetime-cell,
.datetime-container,
.datetime-value {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 100% !important;
}

/* 修复创建时间显示问题 */
.el-descriptions {
  --el-descriptions-item-bordered-label-width: auto;
}

.el-descriptions-item {
  display: flex;
  flex-wrap: nowrap;
}

.el-descriptions__cell {
  max-width: 100%;
}

.datetime-value {
  flex: 1 !important;
  width: 0 !important; /* 允许flex元素缩小到比内容更小 */
}

/* 修复余额不足弹窗的 container 宽度问题 */
.balance-insufficient-dialog .el-message-box__container,
.balance-insufficient-free-dialog .el-message-box__container,
.balance-warning-dialog .el-message-box__container,
.balance-confirm-dialog .el-message-box__container {
  width: 100% !important;
  max-width: 100% !important;
}

/* 确保弹窗本身也有合适的最大宽度 */
.balance-insufficient-dialog,
.balance-insufficient-free-dialog {
  max-width: 500px !important;
}

.balance-warning-dialog,
.balance-confirm-dialog {
  max-width: 600px !important;
}
</style>
